import { type ReactNode, forwardRef } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { zDropdown } from "@/lib/z";
import { cn } from "@/lib/cn";

/** App-wide tooltip provider — mount once near the root. */
export const TooltipProvider = TooltipPrimitive.Provider;
export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, children, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      style={{ zIndex: zDropdown }}
      className={cn(
        "max-w-xs rounded-lg border border-border bg-bg-elevated px-2.5 py-1.5 text-xs text-fg-primary shadow-md",
        "origin-[var(--radix-tooltip-content-transform-origin)] transition-[opacity,transform] duration-150",
        "data-[state=delayed-open]:opacity-100 data-[state=instant-open]:opacity-100 data-[state=closed]:opacity-0",
        "data-[state=delayed-open]:scale-100 data-[state=instant-open]:scale-100 data-[state=closed]:scale-95",
        className,
      )}
      {...props}
    >
      {children}
      <TooltipPrimitive.Arrow className="fill-bg-elevated" />
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = "TooltipContent";

export interface TooltipProps {
  /** The tooltip text/body. */
  content: ReactNode;
  /** Element the tooltip describes. */
  children: ReactNode;
  side?: TooltipPrimitive.TooltipContentProps["side"];
  align?: TooltipPrimitive.TooltipContentProps["align"];
  /** Open delay (ms). Defaults to Radix provider value. */
  delayDuration?: number;
}

/**
 * Tooltip — convenience wrapper (DESIGN-SYSTEM §5). Replaces `title=` attributes
 * (broken on touch). Self-provides its own <Provider> so it is safe standalone:
 * Radix `Root` throws at runtime without a `Provider` ancestor — that
 * previously white-screened the whole authed shell. A global <TooltipProvider>
 * at the app root is also mounted; nesting providers is supported by Radix.
 */
export function Tooltip({
  content,
  children,
  side = "top",
  align = "center",
  delayDuration,
}: TooltipProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration ?? 300}>
      <TooltipPrimitive.Root>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side} align={align}>
          {content}
        </TooltipContent>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
Tooltip.displayName = "Tooltip";
